import React, { ReactNode, useEffect } from 'react';
import {
  ThemeProvider as SharedThemeProvider,
  useTheme as useSharedTheme,
  createThemeStore,
} from '@miror/ui-web/theme';

/**
 * Tema do Miror Task agora delega ao @miror/ui-web/theme:
 * - o provider do shared sincroniza `class="dark"` no <html> (consumido pelo
 *   ui-web + design-tokens);
 * - mantemos a assinatura antiga `{ theme, toggleTheme, setTheme }` para não
 *   tocar nos call sites durante a migração;
 * - `LegacyBodyClassMirror` espelha `body.theme-dark|theme-light`, exigido pelo
 *   CSS legado (global.css + CSS por página). Remover na Fase 5, quando o CSS
 *   legado tiver sido migrado para tokens.
 */

type ThemeMode = 'light' | 'dark';

// storageKey preservada da implementação anterior para não perder a preferência
// já salva dos usuários. defaultMode 'dark' mantém o comportamento atual.
export const themeStore = createThemeStore({
  storageKey: 'mirrorlabs:theme',
  defaultMode: 'dark',
});

const LegacyBodyClassMirror: React.FC = () => {
  const { resolved } = useSharedTheme();
  useEffect(() => {
    const body = document.body;
    body.classList.remove('theme-dark', 'theme-light');
    body.classList.add(`theme-${resolved}`);
  }, [resolved]);
  return null;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <SharedThemeProvider store={themeStore}>
      <LegacyBodyClassMirror />
      {children}
    </SharedThemeProvider>
  );
};

interface ThemeContextData {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

/**
 * Compat hook com a API antiga. `theme` reflete o tema resolvido
 * (light/dark), nunca 'system'.
 */
export const useTheme = (): ThemeContextData => {
  const { resolved, setMode } = useSharedTheme();
  return {
    theme: resolved,
    setTheme: (mode: ThemeMode) => setMode(mode),
    toggleTheme: () => setMode(resolved === 'dark' ? 'light' : 'dark'),
  };
};
